import type { Path } from 'react-router';
import { useLocation } from 'react-router';
import {
  matchExperiments,
  matchFlags,
  matchFlagTargeting,
  matchInsights,
  matchMetrics,
  matchPayloadFilterEdit,
  matchSegments,
  matchViewLinkedResources,
  matchViewLinkedResourcesAdd,
} from '@gonfalon/navigator/matchers';

import { EventLocation } from './types';

/**
 * Safely determines the EventLocation from a router location object.
 * Returns an empty string if the location cannot be determined or if an error occurs.
 */
function getEventLocationFromPath(location: Path): EventLocation | '' {
  try {
    // Order matters: more specific routes should be checked first
    if (matchViewLinkedResourcesAdd(location) || matchViewLinkedResources(location)) {
      return EventLocation.VIEW_LINKED_RESOURCES;
    }

    if (matchFlagTargeting(location)) {
      return EventLocation.FLAG_TARGETING;
    }

    if (matchFlags(location)) {
      return EventLocation.FLAG_LIST;
    }

    if (matchExperiments(location)) {
      return EventLocation.EXPERIMENT_LIST;
    }

    if (matchMetrics(location)) {
      return EventLocation.METRIC_PAGE;
    }

    if (matchInsights(location)) {
      return EventLocation.FLAG_INSIGHTS;
    }

    if (matchPayloadFilterEdit(location)) {
      return EventLocation.PAYLOAD_FILTERS;
    }

    if (matchSegments(location)) {
      return EventLocation.FLAGS_IN_SEGMENT;
    }

    return '';
  } catch {
    // If any matcher throws, fail silently and return empty string
    return '';
  }
}

/**
 * React hook that determines the EventLocation based on the current route.
 * Returns an empty string if the location cannot be determined.
 *
 * Note: This hook must be called within a Router context. The matching logic
 * is wrapped in try-catch to ensure analytics failures don't crash the UI.
 */
export function useEventLocation(): EventLocation | '' {
  const location = useLocation();
  return getEventLocationFromPath(location);
}
